/**
 * @fileoverview Pure synchronous utilities for creating and manipulating form fields.
 * @module ui-admin/services/formFieldUtils
 *
 * Responsibilities:
 * - Create form field objects with default values and IDs.
 * - Provide field type metadata and validation helpers.
 * - Support canvas components in both tenant-web and system-web.
 *
 * Dependencies: None (pure logic, no DB dependencies).
 * Architecture: Stateless utility functions — no side effects.
 * Notes: Used by canvas/builder components for field creation and manipulation.
 */

import type { FormField, DataBoundWidgetDefinition, TemplateType } from '../types/templateTypes';
import { DATA_BOUND_WIDGET_REGISTRY, TEMPLATE_TYPE_TO_ENTITY_CATEGORIES } from '../data/dataBoundWidgetRegistry';

function generateFieldId(): string {
  return 'fld_' + Math.random().toString(36).substring(2, 8);
}

export function generateSectionId(): string {
  return 'sec-' + Math.random().toString(36).substring(2, 8);
}

export function createBlankField(type: FormField['type']): FormField {
  const id = generateFieldId();
  const labels: Record<string, string> = {
    toggle: 'New Toggle', text: 'New Text Field', textarea: 'New Text Area',
    number: 'New Number', measurement: 'New Measurement', dropdown: 'New Dropdown',
    checkboxGroup: 'New Checkbox Group', radioGroup: 'New Radio Group',
    camera: 'New Camera', date: 'New Date', dateTime: 'New Date & Time',
    photo: 'New Photo', signature: 'New Signature', location: 'New Location',
    section: 'New Section', instructions: 'General Information', repeatableList: 'New List',
    dataBound: 'New Data Field', inspectors: 'Inspection Team',
  };
  const field: FormField = {
    id,
    type,
    label: labels[type] || 'New Field',
    required: false,
  };
  if (type === 'dropdown' || type === 'checkboxGroup' || type === 'radioGroup') {
    field.extras = { options: [] };
  }
  if (type === 'section') {
    field.extras = { columns: 1 };
  }
  return field;
}

// ---------------------------------------------------------------------------
// Widget Resolution — Phase 1.4
//
// Given an AI-suggested field label (e.g. "Property Address"), return the
// best-matching data-bound widget from the registry, or null if none fits.
//
// Resolution priority order (per TODO [AI-WIDGET-PREFERENCE] spec):
//   1. Exact label match (case-insensitive)
//   2. Keyword match against widget label and description
//   3. templateType entity category filter — only widgets whose entityCategoryFilter
//      includes a category applicable to the given templateType are considered
//   4. Fall back to null → caller emits a plain FormField
// ---------------------------------------------------------------------------

const KEYWORD_ALIASES: Record<string, string[]> = {
  'entity.name':                     ['entity name', 'location name', 'business name', 'property name'],
  'entity.address.full':             ['property address', 'full address', 'address', 'site address', 'location address'],
  'entity.address.street':           ['street address', 'street'],
  'entity.address.city':             ['city'],
  'entity.address.state':            ['state'],
  'entity.address.zip':              ['zip', 'zip code', 'postal code'],
  'entity.parcelNumber':             ['parcel number', 'apn', 'assessor parcel', 'parcel id'],
  'entity.propertyType':             ['property type', 'occupancy type', 'occupancy class', 'use type'],
  'entity.zoning':                   ['zoning', 'zone', 'zoning code'],
  'entity.lastInspectionDate':       ['last inspection date', 'last inspected', 'previous inspection date'],
  'entity.inspectionFrequency':      ['inspection frequency', 'inspection interval'],
  'entity.inspectionAccountName':    ['inspection account', 'account name'],
  'entity.primaryContact.name':      ['primary contact', 'contact name', 'responsible party', 'owner name', 'property owner'],
  'entity.primaryContact.phone':     ['contact phone', 'owner phone', 'primary phone'],
  'entity.primaryContact.email':     ['contact email', 'owner email', 'primary email'],
  'entity.building.yearBuilt':       ['year built', 'construction year', 'built year'],
  'entity.building.squareFeet':      ['square footage', 'square feet', 'sqft', 'floor area', 'building area'],
  'entity.building.floors':          ['number of floors', 'floors', 'stories', 'num floors'],
  'entity.building.constructionType':['construction type', 'construction classification'],
  'entity.building.sprinklered':     ['sprinklered', 'sprinkler system', 'has sprinklers'],
  'entity.building.buildingType':    ['building type', 'building classification'],
  'entity.hydrant.hydrantNumber':    ['hydrant number', 'hydrant id', 'hydrant #'],
  'entity.hydrant.flowRate':         ['flow rate', 'flow rate (gpm)', 'gpm'],
  'entity.hydrant.hydrantType':      ['hydrant type'],
  'entity.hydrant.installDate':      ['install date', 'installation date', 'hydrant installed'],
  'entity.parcel.acres':             ['acres', 'acreage', 'lot size'],
  'event.inspectionType':            ['inspection type', 'visit type', 'inspection kind'],
  'event.primaryInspectorName':      ['inspector name', 'inspector', 'assigned inspector'],
  'event.scheduledDate':             ['scheduled date', 'inspection date', 'date of inspection'],
  'event.internalNotes':             ['internal notes', 'staff notes', 'office notes'],
  'event.externalNotes':             ['external notes', 'public notes', 'owner notes', 'inspector notes', 'notes'],
  'case.caseNumber':                 ['case number', 'case #', 'case id'],
  'case.caseType':                   ['case type'],
};

export function resolveFieldToWidget(
  label: string,
  templateType: TemplateType,
  hasModule: (key: string) => boolean = () => true,
): DataBoundWidgetDefinition | null {
  const normalized = label.toLowerCase().trim();
  const applicableCategories = TEMPLATE_TYPE_TO_ENTITY_CATEGORIES[templateType] ?? [];

  function isWidgetEligible(w: DataBoundWidgetDefinition): boolean {
    if (w.requiredModule && !hasModule(w.requiredModule)) return false;
    if (w.dataSource === 'inspectableEntity' && w.entityCategoryFilter) {
      if (!w.entityCategoryFilter.some((c) => applicableCategories.includes(c))) return false;
    }
    return true;
  }

  const candidates = DATA_BOUND_WIDGET_REGISTRY.filter(isWidgetEligible);

  for (const w of candidates) {
    if (w.label.toLowerCase() === normalized) return w;
  }

  for (const [widgetId, aliases] of Object.entries(KEYWORD_ALIASES)) {
    if (aliases.some((a) => normalized.includes(a) || a.includes(normalized))) {
      const w = candidates.find((c) => c.widgetId === widgetId);
      if (w) return w;
    }
  }

  for (const w of candidates) {
    const wLabel = w.label.toLowerCase();
    if (wLabel.includes(normalized) || normalized.includes(wLabel)) return w;
  }

  return null;
}

/**
 * Resolve an array of AI-suggested field specs to FormFields, preferring
 * data-bound widgets over plain fields wherever a match is found.
 */
export interface AISuggestedField {
  label: string;
  type: FormField['type'];
  required?: boolean;
}

export function resolveAISuggestionsToFields(
  suggestions: AISuggestedField[],
  templateType: TemplateType,
  hasModule: (key: string) => boolean = () => true,
): FormField[] {
  const timestamp = Date.now();
  return suggestions.map((s, i) => {
    const widget = resolveFieldToWidget(s.label, templateType, hasModule);
    const uniqueId = `ai-field-${timestamp}-${Math.random().toString(36).substring(2, 8)}-${i}`;
    if (widget) {
      const field = createWidgetField(widget);
      field.id = uniqueId;
      field.required = s.required ?? false;
      return field;
    }
    return {
      id: uniqueId,
      label: s.label,
      type: s.type,
      required: s.required ?? false,
      extras: {},
    } as FormField;
  });
}

export function createWidgetField(widget: DataBoundWidgetDefinition): FormField {
  const id = generateFieldId();
  return {
    id,
    type: 'dataBound',
    label: widget.label,
    required: false,
    extras: {
      widgetId: widget.widgetId,
      widgetCategory: widget.widgetCategory,
      dataSource: widget.dataSource,
      boundField: widget.boundField,
      ...(widget.compositeFields ? { compositeFields: widget.compositeFields } : {}),
      displayFormat: widget.displayFormat,
      mode: widget.defaultMode,
      inputType: widget.inputType,
      ...(widget.lookupType ? { lookupType: widget.lookupType } : {}),
      ...(widget.requiredModule ? { requiredModule: widget.requiredModule } : {}),
      ...(widget.entityCategoryFilter ? { entityCategoryFilter: widget.entityCategoryFilter } : {}),
      ...(widget.forceReadOnly ? { forceReadOnly: true } : {}),
    },
  };
}
